/**
 * ChatRoomTransport — the HTTP surface `useChatRoom` needs, behind one seam.
 *
 * The authenticated report talks to `/chat-rooms/*` with a platform JWT; an
 * email-verified guest on `/share/:token` talks to the guest-scoped mirror
 * under `/public/reports/:shareToken/rooms/*`. Both hit the same rooms and the
 * same orchestration on the backend, so swapping the transport is all it takes
 * to run the identical room engine and UI in either context.
 */
import { chatRoomsApi, ChatImage, publicApi } from '@/lib/api'
import {
  ChatRoomDto,
  ChatRoomMessageDto,
  ChatRoomReactionsEvent,
  ChatRoomSettings,
} from '@digital-twins/shared-types'

/** A PDF sent with a message (Step 10c). The twins still read the inlined text in `content`. */
export interface ChatRoomSendDocument {
  base64: string
  name: string
  mimeType: 'application/pdf'
}

export interface ChatRoomSendPayload {
  clientRequestId?: string
  content: string
  mode: 'chat' | 'sample'
  images?: Array<ChatImage>
  captureUrl?: string
  stimulusVideos?: Array<{ url: string; label?: string; mimeType?: string }>
  /** Persona tokens picked as tellers for this send (C2). */
  tellerPicks?: Array<string>
  documents?: Array<ChatRoomSendDocument>
}

export interface ChatRoomTransport {
  /** Namespaces SWR cache keys so owner and guest views never share an entry. */
  scope: string
  /** Only the authenticated room owner can mutate room-level settings. */
  canUpdateSettings: boolean
  get: (token: string) => Promise<ChatRoomDto>
  getMessages: (token: string) => Promise<Array<ChatRoomMessageDto>>
  sendMessage: (token: string, payload: ChatRoomSendPayload) => Promise<{ message?: ChatRoomMessageDto }>
  updateSettings: (token: string, settings: Partial<ChatRoomSettings>) => Promise<ChatRoomDto>
  getImageUrl: (token: string, key: string) => string
  /**
   * C13: the room's resolved reactions snapshot. Optional so a host without a
   * reactions route (or a test double) simply shows no chips.
   */
  getReactions?: (token: string) => Promise<ChatRoomReactionsEvent>
}

// Both endpoints default to the oldest 50 messages. Hydration and legacy send
// acknowledgement recovery need the complete transcript, including later turns.
async function getAllMessages(
  fetchPage: (params: { limit: number; offset: number }) => Promise<Array<ChatRoomMessageDto>>,
): Promise<Array<ChatRoomMessageDto>> {
  const limit = 50
  const messages: Array<ChatRoomMessageDto> = []
  for (;;) {
    const page = await fetchPage({ limit, offset: messages.length })
    messages.push(...page)
    if (page.length < limit) return messages
  }
}

export const authenticatedChatRoomTransport: ChatRoomTransport = {
  scope: 'auth',
  canUpdateSettings: true,
  get: (token) => chatRoomsApi.get(token),
  getMessages: (token) => getAllMessages((params) => chatRoomsApi.getMessages(token, params)),
  sendMessage: (token, payload) => chatRoomsApi.sendMessage(token, payload),
  updateSettings: (token, settings) => chatRoomsApi.updateSettings(token, settings),
  getImageUrl: (token, key) => chatRoomsApi.getImageUrl(token, key),
  getReactions: (token) => chatRoomsApi.getReactions(token),
}

/**
 * Guest transport for a study share link. Settings changes are not exposed to
 * guests — the room is configured at creation time and owned by the study
 * owner — so `updateSettings` resolves to the current room unchanged.
 */
export function createGuestChatRoomTransport(shareToken: string): ChatRoomTransport {
  return {
    scope: `guest:${shareToken}`,
    canUpdateSettings: false,
    get: (token) => publicApi.getRoom(shareToken, token),
    getMessages: (token) => getAllMessages((params) => publicApi.getRoomMessages(shareToken, token, params)),
    sendMessage: (token, payload) =>
      publicApi.sendRoomMessage(shareToken, token, {
        content: payload.content,
        ...(payload.clientRequestId ? { clientRequestId: payload.clientRequestId } : {}),
        ...(payload.images && payload.images.length > 0 ? { images: payload.images } : {}),
      }),
    updateSettings: (token) => publicApi.getRoom(shareToken, token),
    getImageUrl: (token, key) => publicApi.getRoomImageUrl(shareToken, token, key),
    getReactions: (token) => publicApi.getRoomReactions(shareToken, token),
  }
}
